import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { 
  Lock, Eye, EyeOff, Save, FileText, Image, Type, Trash2, 
  Plus, GripVertical, Layers, Monitor, Upload, Copy, ArrowUp, ArrowDown,
  ArrowLeft, Undo2, Redo2, MoreVertical, Menu, X,
  Gamepad2, Search, Building2, Trophy, Star, BookOpen, Megaphone, Smartphone, Mail, Award, Users
} from "lucide-react";

const ADMIN_PASSWORD = "504to713";

// Navigation items with two-line labels
const NAV_ITEMS = [
  { id: "how-it-works", labelTop: "How It", labelBottom: "Works", icon: Gamepad2 },
  { id: "host-game", labelTop: "Host a", labelBottom: "Game", icon: Plus },
  { id: "find-games", labelTop: "Find", labelBottom: "Games", icon: Search },
  { id: "featured", labelTop: "Featured", labelBottom: "Games", icon: Trophy },
  { id: "membership", label: "Membership", icon: Star },
  { id: "discover", label: "Players", icon: Users, isPage: true, path: "/discover" },
  { id: "leaderboard", label: "Leaderboard", icon: Award, isPage: true, path: "/leaderboard" },
  { id: "rules", label: "Rules", icon: BookOpen, isPage: true, path: "/rules" },
  { id: "sponsors", label: "Sponsors", icon: Megaphone, isPage: true, path: "/sponsors" },
  { id: "venues", label: "Venues", icon: Building2, isPage: false },
  { id: "apps", label: "Apps", icon: Smartphone, isPage: true, path: "/apps" },
  { id: "contact", label: "Contact", icon: Mail, isPage: true, path: "/contact" },
];

const AdminPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [pages, setPages] = useState([
    { id: "home", name: "Home", elements: [] },
    { id: "rules", name: "Rules", elements: [] },
    { id: "sponsors", name: "Sponsors", elements: [] },
    { id: "apps", name: "Apps", elements: [] },
    { id: "contact", name: "Contact", elements: [] },
    { id: "terms", name: "Terms", elements: [] },
    { id: "privacy", name: "Privacy", elements: [] },
    { id: "mission", name: "Mission", elements: [] },
    { id: "about", name: "About", elements: [] },
  ]);
  const [selectedPage, setSelectedPage] = useState("home");
  const [selectedElement, setSelectedElement] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Undo/Redo state
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isUndoRedo, setIsUndoRedo] = useState(false);
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, type: null, target: null });

  // Element types available to add
  const elementTypes = [
    { type: "heading", icon: Type, label: "Heading" },
    { type: "paragraph", icon: FileText, label: "Paragraph" },
    { type: "image", icon: Image, label: "Image" },
    { type: "button", icon: Layers, label: "Button" },
  ];

  // Save to history for undo/redo
  const saveToHistory = useCallback((newPages) => {
    if (isUndoRedo) return;
    
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(newPages)));
    
    // Limit history to 50 entries
    if (newHistory.length > 50) {
      newHistory.shift();
    }
    
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex, isUndoRedo]);

  // Initialize history with first state
  useEffect(() => {
    if (authenticated && history.length === 0) {
      setHistory([JSON.parse(JSON.stringify(pages))]);
      setHistoryIndex(0);
    }
  }, [authenticated]);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  const handleUndo = () => {
    if (!canUndo) return;
    setIsUndoRedo(true);
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    setPages(JSON.parse(JSON.stringify(history[newIndex])));
    setSelectedElement(null);
    toast.success("Undo successful");
    setTimeout(() => setIsUndoRedo(false), 100);
  };

  const handleRedo = () => {
    if (!canRedo) return;
    setIsUndoRedo(true);
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    setPages(JSON.parse(JSON.stringify(history[newIndex])));
    setSelectedElement(null);
    toast.success("Redo successful");
    setTimeout(() => setIsUndoRedo(false), 100);
  };

  // Close context menu on click outside
  useEffect(() => {
    const handleClickOutside = () => setContextMenu({ ...contextMenu, visible: false });
    if (contextMenu.visible) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [contextMenu.visible]);

  // Handle right-click context menu
  const handleContextMenu = (e, type, target) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      type,
      target
    });
  };

  // Delete based on context menu type
  const handleContextDelete = () => {
    if (contextMenu.type === "page") {
      // Delete page (not default ones)
      const defaultPages = ["home", "rules", "sponsors", "apps", "contact", "terms", "privacy", "mission", "about"];
      if (!defaultPages.includes(contextMenu.target)) {
        const newPages = pages.filter(p => p.id !== contextMenu.target);
        setPages(newPages);
        saveToHistory(newPages);
        if (selectedPage === contextMenu.target) {
          setSelectedPage("home");
        }
        toast.success("Page deleted");
      } else {
        toast.error("Cannot delete default pages");
      }
    } else if (contextMenu.type === "element") {
      deleteElement(contextMenu.target);
    }
    setContextMenu({ ...contextMenu, visible: false });
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      toast.success("Access granted");
    } else {
      toast.error("Invalid password");
    }
  };

  const getCurrentPageElements = () => {
    const page = pages.find(p => p.id === selectedPage);
    return page ? page.elements : [];
  };

  const updatePageElements = (newElements) => {
    const newPages = pages.map(p => 
      p.id === selectedPage ? { ...p, elements: newElements } : p
    );
    setPages(newPages);
    saveToHistory(newPages);
  };

  const addElement = (type) => {
    const newElement = {
      id: `element-${Date.now()}`,
      type,
      content: type === "heading" ? "New Heading" : 
               type === "paragraph" ? "New paragraph text..." :
               type === "image" ? "https://via.placeholder.com/400x200" :
               "Button Text",
      styles: {
        fontSize: type === "heading" ? "2xl" : "base",
        color: type === "heading" ? "#7a6200" : "#A1A1AA",
        alignment: "left",
      }
    };
    updatePageElements([...getCurrentPageElements(), newElement]);
    setSelectedElement(newElement.id);
    toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} added`);
  };

  const updateElement = (elementId, updates) => {
    const elements = getCurrentPageElements();
    const newElements = elements.map(el => 
      el.id === elementId ? { ...el, ...updates } : el
    );
    updatePageElements(newElements);
  };

  const deleteElement = (elementId) => {
    updatePageElements(getCurrentPageElements().filter(el => el.id !== elementId));
    setSelectedElement(null);
    toast.success("Element deleted");
  };

  const duplicateElement = (elementId) => {
    const elements = getCurrentPageElements();
    const elementToDupe = elements.find(el => el.id === elementId);
    if (elementToDupe) {
      const newElement = {
        ...elementToDupe,
        id: `element-${Date.now()}`,
      };
      const index = elements.findIndex(el => el.id === elementId);
      const newElements = [...elements];
      newElements.splice(index + 1, 0, newElement);
      updatePageElements(newElements);
      toast.success("Element duplicated");
    }
  };

  const moveElement = (elementId, direction) => {
    const elements = getCurrentPageElements();
    const index = elements.findIndex(el => el.id === elementId);
    if ((direction === "up" && index > 0) || (direction === "down" && index < elements.length - 1)) {
      const newElements = [...elements];
      const swapIndex = direction === "up" ? index - 1 : index + 1;
      [newElements[index], newElements[swapIndex]] = [newElements[swapIndex], newElements[index]];
      updatePageElements(newElements);
    }
  };

  const handleDragStart = (e, elementId) => {
    setDraggedItem(elementId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, targetId) => {
    e.preventDefault();
    if (draggedItem && draggedItem !== targetId) {
      const elements = getCurrentPageElements();
      const dragIndex = elements.findIndex(el => el.id === draggedItem);
      const targetIndex = elements.findIndex(el => el.id === targetId);
      
      const newElements = [...elements];
      const [removed] = newElements.splice(dragIndex, 1);
      newElements.splice(targetIndex, 0, removed);
      
      // Update without saving to history during drag
      setPages(pages.map(p => 
        p.id === selectedPage ? { ...p, elements: newElements } : p
      ));
    }
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    // Save to history after drag completes
    saveToHistory(pages);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const pagesData = pages.map(p => ({
        page_id: p.id,
        name: p.name,
        elements: p.elements
      }));
      await axios.post(`${API}/admin/pages/save`, { pages: pagesData });
      localStorage.setItem("b2b_admin_pages", JSON.stringify(pages));
      toast.success("Changes saved successfully!");
    } catch (err) {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    setPreviewMode(!previewMode);
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const pagesData = pages.map(p => ({
        page_id: p.id,
        name: p.name,
        elements: p.elements
      }));
      await axios.post(`${API}/admin/pages/publish`, { pages: pagesData });
      localStorage.setItem("b2b_admin_pages", JSON.stringify(pages));
      toast.success("Changes published successfully!");
    } catch (err) {
      toast.error("Failed to publish changes");
    } finally {
      setPublishing(false);
    }
  };

  const handleNavClick = (item) => {
    if (item.isPage) {
      navigate(item.path);
    } else {
      navigate("/home", { state: { section: item.id } });
    }
    setMobileMenuOpen(false);
  };

  // Load saved pages on mount
  useEffect(() => {
    const loadPages = async () => {
      try {
        const response = await axios.get(`${API}/admin/pages`);
        if (response.data.pages && response.data.pages.length > 0) {
          const backendPages = response.data.pages;
          setPages(prevPages => prevPages.map(p => {
            const backendPage = backendPages.find(bp => bp.page_id === p.id);
            return backendPage ? { ...p, elements: backendPage.elements } : p;
          }));
        }
      } catch (err) {
        const savedPages = localStorage.getItem("b2b_admin_pages");
        if (savedPages) {
          try {
            setPages(JSON.parse(savedPages));
          } catch (e) {
            console.error("Failed to load saved pages");
          }
        }
      }
    };
    loadPages();
  }, []);

  // Password screen
  if (!authenticated) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center px-4"
        style={{
          background: "linear-gradient(135deg, #050505 0%, #0A0A0A 50%, #050505 100%)"
        }}
        data-testid="admin-login-page"
      >
        <Card className="w-full max-w-md bg-[#0A0A0A] border border-white/10">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-[#7a6200]/20 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#7a6200]" />
            </div>
            <CardTitle className="font-bebas text-3xl tracking-wider text-white">
              ADMIN ACCESS
            </CardTitle>
            <p className="text-[#A1A1AA] font-ubuntu text-sm mt-2">
              Enter administrator password to continue
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-dark h-12 pr-12"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <Button
                type="submit"
                className="btn-gold w-full h-12"
                data-testid="admin-login-btn"
              >
                ACCESS ADMIN PANEL
              </Button>
            </form>
            <button 
              onClick={() => navigate(-1)}
              className="w-full mt-4 text-[#52525B] text-sm font-ubuntu hover:text-[#A1A1AA] transition-colors"
            >
              ← Back
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin dashboard
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      {/* Context Menu */}
      {contextMenu.visible && (
        <div 
          className="fixed z-[100] bg-[#1A1A1A] border border-white/20 rounded-lg shadow-xl py-2 min-w-[160px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          data-testid="context-menu"
        >
          <button
            onClick={handleContextDelete}
            className="w-full px-4 py-2 text-left text-red-400 hover:bg-red-500/10 flex items-center gap-2 font-ubuntu text-sm"
            data-testid="context-delete-btn"
          >
            <Trash2 className="w-4 h-4" />
            Delete {contextMenu.type === "page" ? "Page" : "Element"}
          </button>
        </div>
      )}

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-[#050505]/95 backdrop-blur-md">
        <div className="max-w-full mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-20">
            {/* Left section: Back, Logo, Title */}
            <div className="flex items-center gap-4">
              {/* Back Button */}
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-full hover:bg-white/10 text-[#A1A1AA] hover:text-white transition-colors"
                data-testid="admin-back-btn"
                title="Go Back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              
              {/* Logo - 40% larger (62px * 1.4 = 87px) */}
              <img
                src="https://customer-assets.emergentagent.com/job_hoop-invite/artifacts/r8lcaocz_Board2Boston%20favicon.png"
                alt="Board2Boston"
                style={{ height: '87px' }}
                className="cursor-pointer"
                onClick={() => navigate("/home")}
              />
              <h1 className="font-bebas text-2xl tracking-wider text-white">
                ADMIN PANEL
              </h1>
            </div>

            {/* Center section: Navigation (Desktop) */}
            <nav className="hidden xl:flex items-center" style={{ gap: '2px' }}>
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item)}
                  className="px-2 py-1 font-ubuntu text-xs transition-colors text-[#A1A1AA] hover:text-white text-center leading-tight whitespace-nowrap"
                  data-testid={`admin-nav-${item.id}`}
                >
                  {item.labelTop ? (
                    <span className="flex flex-col items-center">
                      <span>{item.labelTop}</span>
                      <span>{item.labelBottom}</span>
                    </span>
                  ) : (
                    item.label
                  )}
                </button>
              ))}
            </nav>
            
            {/* Right section: Undo/Redo, Actions */}
            <div className="flex items-center gap-2">
              {/* Undo/Redo */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleUndo}
                disabled={!canUndo}
                className={`${canUndo ? "text-[#A1A1AA] hover:text-white" : "text-[#52525B] cursor-not-allowed"}`}
                data-testid="admin-undo-btn"
                title="Undo"
              >
                <Undo2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRedo}
                disabled={!canRedo}
                className={`${canRedo ? "text-[#A1A1AA] hover:text-white" : "text-[#52525B] cursor-not-allowed"}`}
                data-testid="admin-redo-btn"
                title="Redo"
              >
                <Redo2 className="w-4 h-4" />
              </Button>

              <div className="w-px h-6 bg-white/10 mx-2" />

              <Button
                variant="outline"
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="border-[#7a6200]/50 text-[#7a6200] hover:bg-[#7a6200]/10"
                data-testid="admin-save-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreview}
                className={`border-white/20 ${previewMode ? "bg-white/10 text-white" : "text-[#A1A1AA]"} hover:bg-white/10`}
                data-testid="admin-preview-btn"
              >
                <Monitor className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button
                size="sm"
                onClick={handlePublish}
                disabled={publishing}
                className="btn-gold"
                data-testid="admin-publish-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {publishing ? "Publishing..." : "Publish"}
              </Button>

              {/* Mobile Menu Toggle */}
              <button
                className="xl:hidden text-white ml-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="xl:hidden border-t border-white/10 bg-[#0A0A0A]">
            <nav className="px-4 py-4 space-y-2">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item)}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded font-ubuntu text-sm text-[#A1A1AA] hover:bg-white/5"
                >
                  <item.icon className="w-5 h-5" />
                  {item.labelTop ? `${item.labelTop} ${item.labelBottom}` : item.label}
                </button>
              ))}
            </nav>
          </div>
        )}
      </header>

      <div className="pt-20 flex">
        {/* Sidebar - Pages & Elements */}
        <aside className="fixed left-0 top-20 bottom-0 w-64 border-r border-white/10 bg-[#0A0A0A] overflow-y-auto">
          {/* Pages Section */}
          <div className="p-4 border-b border-white/10">
            <h3 className="font-bebas text-lg tracking-wider text-[#7a6200] mb-3">PAGES</h3>
            <div className="space-y-1">
              {pages.map((page) => (
                <button
                  key={page.id}
                  onClick={() => setSelectedPage(page.id)}
                  onContextMenu={(e) => handleContextMenu(e, "page", page.id)}
                  className={`w-full text-left px-3 py-2 rounded font-ubuntu text-sm transition-colors flex items-center justify-between group ${
                    selectedPage === page.id
                      ? "bg-[#7a6200]/20 text-[#7a6200]"
                      : "text-[#A1A1AA] hover:bg-white/5"
                  }`}
                  data-testid={`page-select-${page.id}`}
                >
                  <span>{page.name}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleContextMenu(e, "page", page.id);
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-white/10"
                    title="Page options"
                  >
                    <MoreVertical className="w-3 h-3" />
                  </button>
                </button>
              ))}
            </div>
          </div>

          {/* Add Elements Section */}
          <div className="p-4">
            <h3 className="font-bebas text-lg tracking-wider text-[#7a6200] mb-3">ADD ELEMENTS</h3>
            <div className="grid grid-cols-2 gap-2">
              {elementTypes.map(({ type, icon: Icon, label }) => (
                <button
                  key={type}
                  onClick={() => addElement(type)}
                  className="flex flex-col items-center gap-2 p-3 rounded border border-white/10 text-[#A1A1AA] hover:border-[#7a6200]/50 hover:text-[#7a6200] transition-colors"
                  data-testid={`add-${type}-btn`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-xs font-ubuntu">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="ml-64 flex-1 p-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-bebas text-2xl tracking-wider text-white">
                {pages.find(p => p.id === selectedPage)?.name || "Page"} - {previewMode ? "Preview" : "Edit"}
              </h2>
              <span className="text-[#A1A1AA] text-sm font-ubuntu">
                {getCurrentPageElements().length} elements
              </span>
            </div>

            {/* Canvas Area */}
            <div 
              className={`min-h-[600px] rounded-lg border ${previewMode ? "border-[#7a6200]/30" : "border-white/10 border-dashed"} bg-[#0A0A0A] p-6`}
              data-testid="admin-canvas"
            >
              {getCurrentPageElements().length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-[#52525B]">
                  <Layers className="w-12 h-12 mb-4 opacity-50" />
                  <p className="font-ubuntu text-lg">No elements yet</p>
                  <p className="font-ubuntu text-sm mt-1">Add elements from the sidebar to get started</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {getCurrentPageElements().map((element) => (
                    <div
                      key={element.id}
                      draggable={!previewMode}
                      onDragStart={(e) => handleDragStart(e, element.id)}
                      onDragOver={(e) => handleDragOver(e, element.id)}
                      onDragEnd={handleDragEnd}
                      onClick={() => !previewMode && setSelectedElement(element.id)}
                      onContextMenu={(e) => !previewMode && handleContextMenu(e, "element", element.id)}
                      className={`group relative p-4 rounded ${
                        !previewMode ? "cursor-move hover:bg-white/5" : ""
                      } ${
                        selectedElement === element.id && !previewMode
                          ? "ring-2 ring-[#7a6200] bg-[#7a6200]/5"
                          : ""
                      }`}
                      data-testid={`element-${element.id}`}
                    >
                      {/* Element Actions (Edit Mode Only) */}
                      {!previewMode && (
                        <div className="absolute -top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => { e.stopPropagation(); moveElement(element.id, "up"); }}
                            className="p-1 rounded bg-[#0A0A0A] border border-white/20 text-[#A1A1AA] hover:text-white"
                            title="Move up"
                          >
                            <ArrowUp className="w-3 h-3" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); moveElement(element.id, "down"); }}
                            className="p-1 rounded bg-[#0A0A0A] border border-white/20 text-[#A1A1AA] hover:text-white"
                            title="Move down"
                          >
                            <ArrowDown className="w-3 h-3" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); duplicateElement(element.id); }}
                            className="p-1 rounded bg-[#0A0A0A] border border-white/20 text-[#A1A1AA] hover:text-white"
                            title="Duplicate"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteElement(element.id); }}
                            className="p-1 rounded bg-[#0A0A0A] border border-red-500/50 text-red-400 hover:text-red-300"
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      )}

                      {/* Drag Handle */}
                      {!previewMode && (
                        <div className="absolute left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-[#52525B]">
                          <GripVertical className="w-4 h-4" />
                        </div>
                      )}

                      {/* Element Content */}
                      {element.type === "heading" && (
                        <h2 
                          className={`font-bebas text-${element.styles.fontSize} tracking-wider`}
                          style={{ color: element.styles.color, textAlign: element.styles.alignment }}
                        >
                          {element.content}
                        </h2>
                      )}
                      {element.type === "paragraph" && (
                        <p 
                          className={`font-ubuntu text-${element.styles.fontSize}`}
                          style={{ color: element.styles.color, textAlign: element.styles.alignment }}
                        >
                          {element.content}
                        </p>
                      )}
                      {element.type === "image" && (
                        <img 
                          src={element.content} 
                          alt="Content" 
                          className="max-w-full h-auto rounded"
                          style={{ display: "block", margin: element.styles.alignment === "center" ? "0 auto" : element.styles.alignment === "right" ? "0 0 0 auto" : "0" }}
                        />
                      )}
                      {element.type === "button" && (
                        <div style={{ textAlign: element.styles.alignment }}>
                          <button className="btn-gold px-6 py-3 font-bebas tracking-wider">
                            {element.content}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>

        {/* Right Sidebar - Element Properties */}
        {selectedElement && !previewMode && (
          <aside className="fixed right-0 top-20 bottom-0 w-72 border-l border-white/10 bg-[#0A0A0A] overflow-y-auto">
            <div className="p-4">
              <h3 className="font-bebas text-lg tracking-wider text-[#7a6200] mb-4">ELEMENT PROPERTIES</h3>
              
              {(() => {
                const element = getCurrentPageElements().find(el => el.id === selectedElement);
                if (!element) return null;

                return (
                  <div className="space-y-4">
                    {/* Content */}
                    <div>
                      <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                        {element.type === "image" ? "Image URL" : "Content"}
                      </label>
                      {element.type === "paragraph" ? (
                        <Textarea
                          value={element.content}
                          onChange={(e) => updateElement(element.id, { content: e.target.value })}
                          className="input-dark min-h-[100px]"
                          data-testid="element-content-textarea"
                        />
                      ) : (
                        <Input
                          value={element.content}
                          onChange={(e) => updateElement(element.id, { content: e.target.value })}
                          className="input-dark"
                          data-testid="element-content-input"
                        />
                      )}
                    </div>

                    {/* Font Size */}
                    {(element.type === "heading" || element.type === "paragraph") && (
                      <div>
                        <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">Font Size</label>
                        <select
                          value={element.styles.fontSize}
                          onChange={(e) => updateElement(element.id, { 
                            styles: { ...element.styles, fontSize: e.target.value } 
                          })}
                          className="input-dark w-full h-10"
                          data-testid="element-fontsize-select"
                        >
                          <option value="sm">Small</option>
                          <option value="base">Base</option>
                          <option value="lg">Large</option>
                          <option value="xl">XL</option>
                          <option value="2xl">2XL</option>
                          <option value="3xl">3XL</option>
                          <option value="4xl">4XL</option>
                        </select>
                      </div>
                    )}

                    {/* Color */}
                    {element.type !== "image" && element.type !== "button" && (
                      <div>
                        <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">Color</label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={element.styles.color}
                            onChange={(e) => updateElement(element.id, { 
                              styles: { ...element.styles, color: e.target.value } 
                            })}
                            className="w-12 h-10 p-1 bg-transparent border border-white/20 rounded cursor-pointer"
                            data-testid="element-color-input"
                          />
                          <Input
                            value={element.styles.color}
                            onChange={(e) => updateElement(element.id, { 
                              styles: { ...element.styles, color: e.target.value } 
                            })}
                            className="input-dark flex-1"
                          />
                        </div>
                      </div>
                    )}

                    {/* Alignment */}
                    <div>
                      <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">Alignment</label>
                      <div className="flex gap-2">
                        {["left", "center", "right"].map((align) => (
                          <button
                            key={align}
                            onClick={() => updateElement(element.id, { 
                              styles: { ...element.styles, alignment: align } 
                            })}
                            className={`flex-1 py-2 rounded border text-sm font-ubuntu capitalize transition-colors ${
                              element.styles.alignment === align
                                ? "border-[#7a6200] text-[#7a6200] bg-[#7a6200]/10"
                                : "border-white/20 text-[#A1A1AA] hover:border-white/40"
                            }`}
                            data-testid={`align-${align}-btn`}
                          >
                            {align}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Delete Button */}
                    <Button
                      variant="outline"
                      onClick={() => deleteElement(element.id)}
                      className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10 mt-6"
                      data-testid="delete-element-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Element
                    </Button>
                  </div>
                );
              })()}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
